import { type ReactElement } from 'react';
import { ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { cn } from '@/lib/utils';

import type { AdminConsentTypeViewModel } from '../api';

const EMPTY_CELL = '—';

// Row height is a floor, not a lock — descriptions and long names wrap to a
// second line (`line-clamp-2`) so nothing gets cut off with an ellipsis on
// the primary content columns. Header stays fixed height.
const COMPACT_CELL_CLASS = 'min-h-[40px] py-1.5';
const COMPACT_HEAD_CLASS = 'h-[40px] py-0';
const ZEBRA_ROW_CLASS = 'even:bg-muted/55';

interface AdminConsentTypesTableProps {
  readonly rows: readonly AdminConsentTypeViewModel[];
  /**
   * When false, both reorder buttons render disabled. Set to `false` while a
   * client-side search is active — the visible list is a subset, so swapping
   * "adjacent" rows would move non-adjacent items in the underlying data.
   */
  readonly reorderEnabled: boolean;
  readonly onEdit: (row: AdminConsentTypeViewModel) => void;
  readonly onMoveUp: (row: AdminConsentTypeViewModel) => void;
  readonly onMoveDown: (row: AdminConsentTypeViewModel) => void;
  readonly onToggleRequired: (row: AdminConsentTypeViewModel) => void;
  readonly onToggleActive: (row: AdminConsentTypeViewModel) => void;
  readonly pendingUpdateId: string | null;
}

export function AdminConsentTypesTable({
  rows,
  reorderEnabled,
  onEdit,
  onMoveUp,
  onMoveDown,
  onToggleRequired,
  onToggleActive,
  pendingUpdateId,
}: AdminConsentTypesTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <DataTableTable aria-label={t('routes.admin.consentTypes.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[26%] min-w-[10rem]">
            {t('routes.admin.consentTypes.table.name')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[44%] min-w-[12rem]">
            {t('routes.admin.consentTypes.table.description')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[8%] min-w-[4.5rem]">
            {t('routes.admin.consentTypes.table.reorder')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[8%] min-w-[4rem]">
            {t('routes.admin.consentTypes.table.required')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[8%] min-w-[4rem]">
            {t('routes.admin.consentTypes.table.active')}
          </DataTableTh>
          <DataTableTh className={COMPACT_HEAD_CLASS} widthClassName="w-[6%] min-w-[3rem]" align="right">
            <span className="sr-only">{t('routes.admin.consentTypes.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row, index) => (
          <Row
            key={row.id}
            row={row}
            isFirst={!reorderEnabled || index === 0}
            isLast={!reorderEnabled || index === rows.length - 1}
            onEdit={onEdit}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onToggleRequired={onToggleRequired}
            onToggleActive={onToggleActive}
            isSaving={pendingUpdateId === row.id}
          />
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

interface RowProps {
  readonly row: AdminConsentTypeViewModel;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly onEdit: (row: AdminConsentTypeViewModel) => void;
  readonly onMoveUp: (row: AdminConsentTypeViewModel) => void;
  readonly onMoveDown: (row: AdminConsentTypeViewModel) => void;
  readonly onToggleRequired: (row: AdminConsentTypeViewModel) => void;
  readonly onToggleActive: (row: AdminConsentTypeViewModel) => void;
  readonly isSaving: boolean;
}

function Row({
  row,
  isFirst,
  isLast,
  onEdit,
  onMoveUp,
  onMoveDown,
  onToggleRequired,
  onToggleActive,
  isSaving,
}: RowProps): ReactElement {
  const { t } = useTranslation();
  const editLabel = t('routes.admin.consentTypes.row.editAria', { name: row.name });
  // Inactive rows render at reduced opacity so they're visibly de-emphasised.
  const rowClassName = cn(ZEBRA_ROW_CLASS, !row.isActive && 'opacity-60');
  return (
    <DataTableTr className={rowClassName}>
      <DataTableTd className={`${COMPACT_CELL_CLASS} font-medium text-foreground`} dataOffset={false}>
        <span className="block max-w-[16rem] line-clamp-2 leading-snug" title={row.name}>
          {row.name.length > 0 ? row.name : EMPTY_CELL}
        </span>
        {isSaving && (
          <p className="-mb-0.5 mt-0.5 text-[10px] leading-none text-muted-foreground">
            {t('routes.admin.consentTypes.row.saving')}
          </p>
        )}
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        {/* Wraps to two lines then ellipsises anything longer — keeps the
            actions column visible on wide rows without hiding common-case
            descriptions behind a tooltip. Full text stays available on
            hover for the >2-line minority. */}
        <span
          className="block max-w-[28rem] line-clamp-2 leading-snug text-foreground/80"
          title={row.description}
        >
          {row.description.length > 0 ? row.description : EMPTY_CELL}
        </span>
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        {/* Segmented pill so the two chevrons read as a paired control at
            rest, not decorative glyphs. Shared border + divider between
            halves; hover fills each half individually. */}
        <div
          role="group"
          aria-label={t('routes.admin.consentTypes.row.reorderAria', { name: row.name })}
          className="inline-flex overflow-hidden rounded-md border border-input bg-background shadow-sm"
        >
          <button
            type="button"
            onClick={() => {
              onMoveUp(row);
            }}
            disabled={isFirst}
            aria-label={t('routes.admin.consentTypes.row.moveUpAria', { name: row.name })}
            title={t('routes.admin.consentTypes.row.moveUp')}
            className="flex h-6 w-7 items-center justify-center border-r border-input text-foreground/70 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => {
              onMoveDown(row);
            }}
            disabled={isLast}
            aria-label={t('routes.admin.consentTypes.row.moveDownAria', { name: row.name })}
            title={t('routes.admin.consentTypes.row.moveDown')}
            className="flex h-6 w-7 items-center justify-center text-foreground/70 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        <input
          type="checkbox"
          checked={row.isRequired}
          onChange={() => {
            onToggleRequired(row);
          }}
          aria-label={t('routes.admin.consentTypes.row.toggleRequiredAria', { name: row.name })}
          className="h-3.5 w-3.5 cursor-pointer accent-primary"
        />
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} dataOffset>
        <input
          type="checkbox"
          checked={row.isActive}
          onChange={() => {
            onToggleActive(row);
          }}
          aria-label={t('routes.admin.consentTypes.row.toggleActiveAria', { name: row.name })}
          className="h-3.5 w-3.5 cursor-pointer accent-primary"
        />
      </DataTableTd>
      <DataTableTd className={COMPACT_CELL_CLASS} align="right" dataOffset>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => {
            onEdit(row);
          }}
          aria-label={editLabel}
          title={editLabel}
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DataTableTd>
    </DataTableTr>
  );
}
